import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { BookOpen, Award, MessageSquare, Plus, Users, BarChart, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {api} from '../../api';

interface CreatedCourse {
  _id: string;
  title: string;
  thumbnail?: string;
  isPublished: boolean;
  createdAt: string;
}

interface CreatedQuiz {
  _id: string;
  title: string;
  assignedToType: string;
  createdAt: string;
}

interface DashboardStats {
  courseCount: number;
  quizCount: number;
  studentCount: number;
}

const ProfessorDashboard: React.FC = () => {
  const [createdCourses, setCreatedCourses] = useState<CreatedCourse[]>([]);
  const [createdQuizzes, setCreatedQuizzes] = useState<CreatedQuiz[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    courseCount: 0,
    quizCount: 0,
    studentCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    console.log("Starting data loading...");
    try {
      const endpoints = [
        '/professor/dashboard/courses',
        '/professor/dashboard/quizzes',
        '/professor/dashboard/stats'
      ];
      
      // Add error handling for each request
      const [coursesResponse, quizzesResponse, statsResponse] = await Promise.all(
        endpoints.map(endpoint => 
          api.get(endpoint).catch(error => {
            console.error(`Error fetching ${endpoint}:`, error);
            throw error; // Re-throw to be caught by the outer catch
          })
        )
      );
      
      setCreatedCourses(coursesResponse.data.courses || []);
      setCreatedQuizzes(quizzesResponse.data.quizzes || []);
      setStats(statsResponse.data.stats || {
        courseCount: 0,
        quizCount: 0,
        studentCount: 0
      });
    } catch (error: any) {
      console.error('Error details:', { 
        error: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : error.message 
      });
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  // Trier les cours et quiz par date de création (du plus récent au plus ancien)
  const sortedCourses = [...createdCourses].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  
  const sortedQuizzes = [...createdQuizzes].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord Professeur</h1>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link to="/professor/courses" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <BookOpen className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Mes Cours</span>
          </Link>
          <Link to="/professor/quizzes" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <Award className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Mes Quiz</span>
          </Link>
          <Link to="/professor/courses/create" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <Plus className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Créer un Cours</span>
          </Link>
          <Link to="/professor/quizzes/create" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <Plus className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Créer un Quiz</span>
          </Link>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <BookOpen className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Cours Créés</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.courseCount}</h2>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <Award className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Quiz Créés</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.quizCount}</h2>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <Users className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Étudiants Inscrits</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.studentCount}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Courses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes Derniers Cours</h2>
          {sortedCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedCourses.slice(0, 3).map(course => (
                <Link to={`/professor/courses/${course._id}`} key={course._id} className="block border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {course.thumbnail && (
                    <img 
                      src={course.thumbnail} 
                      alt={course.title} 
                      className="w-full h-32 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/path/to/default-image.jpg';
                      }}
                    />
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{course.title}</h3>
                    <p className="text-sm text-gray-600">{course.isPublished ? 'Publié' : 'Brouillon'}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3" />
              <p>Vous n'avez pas encore créé de cours.</p>
              <Link to="/professor/courses/create" className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                Créer votre premier cours
              </Link>
            </div>
          )}
        </div>

        {/* Recent Quizzes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes Derniers Quiz</h2>
          {sortedQuizzes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedQuizzes.slice(0, 3).map(quiz => (
                <Link to={`/professor/quizzes/${quiz._id}`} key={quiz._id} className="block border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-1 truncate">{quiz.title}</h3>
                  <p className="text-sm text-gray-600">Assigné à: {quiz.assignedToType}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3" />
              <p>Vous n'avez pas encore créé de quiz.</p>
              <Link to="/professor/quizzes/create" className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                Créer votre premier quiz
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProfessorDashboard;